import { Check, CheckCheck, ImageOff, Package } from 'lucide-react';
import { formatClockTime, formatPrice } from '../../lib/format';

const STATUS_LABEL = {
  sent: 'Sent',
  seen: 'Seen',
};

/** The server-authored first message of a listing chat. */
function ContextCard({ metadata, mine }) {
  return (
    <div
      className={`flex items-center gap-2.5 rounded-xl border p-2 ${
        mine ? 'border-white/25 bg-white/10' : 'border-line bg-surface-2'
      }`}
    >
      {metadata?.listingImage ? (
        <img
          src={metadata.listingImage}
          alt=""
          width="40"
          height="40"
          loading="lazy"
          className="h-10 w-10 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-surface-3 text-ink-3">
          <Package size={16} />
        </span>
      )}
      <span className="min-w-0">
        <span className="line-clamp-2 block text-[12px] leading-snug font-medium [overflow-wrap:anywhere]">
          {metadata?.listingTitle ?? 'Listing'}
        </span>
        {metadata?.listingPrice != null && (
          <span className="text-[11.5px] tabular-nums opacity-80">
            {formatPrice(metadata.listingPrice)}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * One message.
 *
 * Own messages: right-aligned, brand purple, white text.
 * Received: left-aligned, surface card (white in light mode, dark card in dark).
 *
 * Status is only ever "Sent" or "Seen" - the API records read receipts but has
 * no delivery receipt, so the UI never claims "delivered".
 */
export default function MessageBubble({ message, isLastSent = false, onOpenImage }) {
  const { mine, text, images = [], at, status, messageType, metadata, isDeleted } = message;
  const hasText = text.trim().length > 0;
  const isContextCard = messageType === 'CONTEXT_CARD';

  if (isDeleted) {
    return (
      <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
        <p className="rounded-2xl border border-dashed border-line-2 px-3.5 py-2 text-[12px] text-ink-3 italic">
          Message deleted
        </p>
      </div>
    );
  }

  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      {/* min-w-0 on both the column and the bubble: a flex item's automatic
          minimum size is its min-content width, and one very long unbroken word
          would otherwise stretch the bubble past its max-width and make the
          thread scroll sideways. */}
      <div
        className={`flex min-w-0 max-w-[min(560px,80%)] flex-col ${mine ? 'items-end' : 'items-start'}`}
      >
        <div
          className={`animate-z2-rise min-w-0 rounded-2xl shadow-soft ${
            hasText || images.length > 0 ? 'px-3.5 py-2.5' : 'p-1.5'
          } ${
            mine
              ? 'rounded-br-md bg-brand-600 text-white'
              : 'rounded-bl-md border border-line bg-surface text-ink'
          }`}
        >
          {isContextCard ? (
            <div className="min-w-[176px] space-y-1.5 sm:min-w-[210px]">
              <ContextCard metadata={metadata} mine={mine} />
              {hasText && (
                <p className="text-[11.5px] leading-snug opacity-80">{text}</p>
              )}
            </div>
          ) : (
            <>
              {images.length > 0 && (
                <div className={`grid gap-1.5 ${hasText ? 'mb-2' : ''}`}>
                  {images.map((src, index) => (
                    <button
                      key={`${src}-${index}`}
                      type="button"
                      onClick={() => onOpenImage(src)}
                      aria-label="Open image full size"
                      className="group relative block overflow-hidden rounded-xl"
                    >
                      <img
                        src={src}
                        alt="Shared attachment"
                        loading="lazy"
                        className="h-auto max-h-[230px] w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                      />
                      <span
                        aria-hidden="true"
                        className="absolute inset-0 bg-black/0 transition group-hover:bg-black/10"
                      />
                    </button>
                  ))}
                  {images.some((src) => !src) && (
                    <span className="grid h-16 place-items-center rounded-xl bg-surface-3 text-ink-3">
                      <ImageOff size={16} />
                    </span>
                  )}
                </div>
              )}

              {hasText && (
                // `overflow-wrap: anywhere` (not `break-words`): only `anywhere`
                // also shrinks the min-content width, which is what stops one
                // very long unbroken word from widening the bubble past its
                // max-width and making the thread scroll sideways.
                <p className="text-[13px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">
                  {text}
                </p>
              )}
            </>
          )}
        </div>

        <div
          className={`mt-1 flex items-center gap-1.5 px-1 text-[10.5px] text-ink-3 ${
            mine ? 'flex-row-reverse' : ''
          }`}
        >
          <span className="tabular-nums">{formatClockTime(at)}</span>

          {isLastSent && status && (
            <span
              className={`inline-flex items-center gap-0.5 font-medium ${
                status === 'seen' ? 'text-accent' : ''
              }`}
            >
              {status === 'sent' ? <Check size={11} /> : <CheckCheck size={11} />}
              {STATUS_LABEL[status]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
